import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
void motion;

/**
 * Page Transition Component
 * 
 * Page transition wrapper with:
 * - Smooth page transitions
 * - Multiple transition types (fade, slide, scale)
 * - Blur effects
 * - Staggered children animations
 * 
 * @component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Page content
 * @returns {JSX.Element} The page transition wrapper
 */
const PageTransition = ({ children }) => {
  const { _t } = useTranslation();

  const location = useLocation();

  // Simplified page variants - disabled blur and opacity animations to prevent blank page issues
  // The framer-motion animations were getting stuck at initial state causing pages to be invisible
  const pageVariants = {
    initial: {
      opacity: 1,
      scale: 1,
      y: 0,
      filter: 'none'
    },
    in: {
      opacity: 1,
      scale: 1,
      y: 0,
      filter: 'none',
      transition: {
        duration: 0.1,
        ease: [0.4, 0, 0.2, 1]
      }
    },
    out: {
      opacity: 1,
      scale: 1,
      y: 0,
      filter: 'none',
      transition: {
        duration: 0.1,
        ease: [0.4, 0, 0.2, 1]
      }
    }
  };

  // Simplified slide variants - disabled opacity animations to prevent blank page issues
  const slideVariants = {
    initial: {
      x: 0,
      opacity: 1
    },
    in: {
      x: 0,
      opacity: 1,
      transition: {
        duration: 0.1,
        ease: [0.4, 0, 0.2, 1]
      }
    },
    out: {
      x: 0,
      opacity: 1,
      transition: {
        duration: 0.1,
        ease: [0.4, 0, 0.2, 1]
      }
    }
  };

  // Simplified morph variants - disabled clipPath and opacity animations to prevent blank page issues
  const morphVariants = {
    initial: {
      clipPath: 'none',
      opacity: 1
    },
    in: {
      clipPath: 'none',
      opacity: 1,
      transition: {
        duration: 0.1,
        ease: [0.4, 0, 0.2, 1]
      }
    },
    out: {
      clipPath: 'none',
      opacity: 1,
      transition: {
        duration: 0.1,
        ease: [0.4, 0, 0.2, 1]
      }
    }
  };

  // Disable animations for workstation pages to prevent blank page issues
  const noAnimationVariants = {
    initial: { opacity: 1 },
    in: { opacity: 1 },
    out: { opacity: 1 }
  };

  // Choose transition type based on route
  const getTransitionType = (pathname) => {
    // Disable animations for workstation pages - they have their own styling
    if (pathname.includes('/workstation')) return noAnimationVariants;
    if (pathname.includes('/search')) return slideVariants;
    if (pathname.includes('/client/dashboard')) return morphVariants;
    return pageVariants;
  };

  const currentVariants = getTransitionType(location.pathname);

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={location.pathname}
        initial="initial"
        animate="in"
        exit="out"
        variants={currentVariants}
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          overflow: 'hidden'
        }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
};

export default React.memo(PageTransition);

